import type { CodeString } from '@/consts/codes';
import type { MenuItem } from '@/consts/navigation';
import type { TranslationKey } from '@/i18n/i18n';

interface RouteInfo {
  route?: string;
  code?: CodeString;
  label: TranslationKey;
  parent?: TranslationKey;
  parentChain?: TranslationKey[];
  isRedirectOnly?: boolean;
}

export const findMatchingRoute = (fullPath: string, routes: RouteInfo[]): RouteInfo | undefined => {
  // Exact match (handles both clean paths and paths with query strings)
  let match = routes.find((r) => r.route === fullPath);

  // Try matching without query strings for parameterized routes
  if (!match) {
    const pathname = fullPath.split('?')[0] ?? fullPath;
    match = routes.find((r) => {
      if (!r.route) return false;
      const routePath = r.route.split('?')[0] ?? r.route;
      const pattern = routePath.replace(/:\w+/g, '[^/]+');
      return new RegExp(`^${pattern}$`).test(pathname);
    });
  }

  return match;
};

const buildRoutesRecursive = (item: MenuItem, parentChain: TranslationKey[] = []): RouteInfo[] => {
  const routes: RouteInfo[] = [];
  const currentChain = [...parentChain, item.text];

  if (item.to) {
    const hasChildren = Boolean(item.children?.length);
    routes.push({
      route: item.to,
      label: item.text,
      code: item.code,
      parent: parentChain[parentChain.length - 1],
      parentChain: parentChain.length > 0 ? parentChain : undefined,
      isRedirectOnly: hasChildren
    });
  }

  if (item.children) {
    for (const child of item.children) {
      routes.push(...buildRoutesRecursive(child, currentChain));
    }
  }

  return routes;
};

export const buildRouteMapping = (menu: MenuItem[]): RouteInfo[] => {
  return menu.flatMap<RouteInfo>((section) => {
    // Group headers (OPERATIONS, TECHNICAL, etc.) are not navigation levels —
    // skip them in the parent chain and process their children directly.
    if (section.isGroup && section.children) {
      return section.children.flatMap((child) => {
        if (child.children) {
          return child.children.flatMap((grandchild) => buildRoutesRecursive(grandchild, [child.text]));
        }
        return child.to ? [{ label: child.text, route: child.to }] : [];
      });
    }

    if (section.children) {
      return section.children.flatMap((child) => buildRoutesRecursive(child, [section.text]));
    }

    return section.to ? [{ label: section.text, route: section.to }] : [];
  });
};
